import React, { useState, useEffect } from 'react';
import { Typography, FlexGrid, Notification } from '@telus-uds/components-web';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

import { reportsAPI, exportReportExcel } from '../utils/apiService';
import { getApiErrorMessage } from '../utils/apiErrorMessage';
import {
  formatNumber,
  formatMilliseconds,
  getDefaultDateRange,
  getPerformanceColor
} from '../utils/formatters';
import SummaryCard from '../components/SummaryCard';
import FilterPanel from '../components/FilterPanel';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import DataTable from '../components/DataTable';
import DrillDownModal from '../components/DrillDownModal';
import { chartCardStyle, CHART_COLORS as COLORS } from '../utils/chartStyles';

const ApiRequestsReport = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [drillDownCategory, setDrillDownCategory] = useState(null);
  const [filterValues, setFilterValues] = useState({
    startDate: getDefaultDateRange().startDate,
    endDate: getDefaultDateRange().endDate,
    method: '',
    endpointCategory: '',
    statusCode: '',
    userId: '',
    excludeScanners: 'false'
  });
  // Snapshot of whichever filters produced the data currently on screen — export must use
  // this, not the live filterValues, or editing a filter without clicking Apply would export
  // a file that doesn't match what's displayed.
  const [appliedFilters, setAppliedFilters] = useState(null);

  const fetchData = async (customFilters = null) => {
    setLoading(true);
    setError(null);
    try {
      const filters = customFilters || filterValues;
      setAppliedFilters(filters);
      const result = await reportsAPI.getApiRequests(filters);
      setData(result.data);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to fetch API requests data'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleFilterChange = (name, value) => {
    setFilterValues(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    fetchData();
  };

  const handleResetFilters = () => {
    const defaultFilters = {
      startDate: getDefaultDateRange().startDate,
      endDate: getDefaultDateRange().endDate,
      method: '',
      endpointCategory: '',
      statusCode: '',
      userId: '',
      excludeScanners: 'false'
    };
    setFilterValues(defaultFilters);
    // Fetch data immediately with default filters
    fetchData(defaultFilters);
  };

  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      const exportFilters = appliedFilters || filterValues;
      const fileName = `api-requests-report-${exportFilters.startDate}-to-${exportFilters.endDate}.xlsx`;
      await exportReportExcel('/reports/api-requests/export/excel', exportFilters, fileName);
    } catch (err) {
      alert('Failed to export Excel file. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const filters = [
    {
      name: 'startDate',
      label: 'Start Date',
      type: 'date',
      value: filterValues.startDate,
      placeholder: 'YYYY-MM-DD'
    },
    {
      name: 'endDate',
      label: 'End Date',
      type: 'date',
      value: filterValues.endDate,
      placeholder: 'YYYY-MM-DD'
    },
    {
      name: 'method',
      label: 'HTTP Method',
      type: 'select',
      value: filterValues.method,
      options: [
        { value: '', label: 'All' },
        { value: 'GET', label: 'GET' },
        { value: 'POST', label: 'POST' },
        { value: 'PUT', label: 'PUT' },
        { value: 'DELETE', label: 'DELETE' },
        { value: 'PATCH', label: 'PATCH' }
      ]
    },
    {
      name: 'endpointCategory',
      label: 'Category',
      type: 'select',
      value: filterValues.endpointCategory,
      options: [
        { value: '', label: 'All' },
        { value: 'orders', label: 'Orders' },
        { value: 'users', label: 'Users' },
        { value: 'products', label: 'Products' },
        { value: 'reports', label: 'Reports' }
      ]
    },
    {
      name: 'statusCode',
      label: 'Status Code',
      type: 'select',
      value: filterValues.statusCode,
      options: [
        { value: '', label: 'All' },
        { value: '200', label: '200 - OK' },
        { value: '201', label: '201 - Created' },
        { value: '400', label: '400 - Bad Request' },
        { value: '404', label: '404 - Not Found' },
        { value: '500', label: '500 - Server Error' }
      ]
    },
    {
      name: 'excludeScanners',
      label: 'Traffic Source',
      type: 'select',
      value: filterValues.excludeScanners,
      options: [
        { value: 'false', label: 'Include all traffic' },
        { value: 'true', label: 'Exclude scanner/bot probes' }
      ]
    }
  ];

  if (loading) {
    return (
      <div>
        <FilterPanel filters={filters} onFilterChange={handleFilterChange} onApply={handleApplyFilters} onReset={handleResetFilters} />
        <LoadingSpinner text="Loading API Requests Report..." />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div>
        <FilterPanel filters={filters} onFilterChange={handleFilterChange} onApply={handleApplyFilters} onReset={handleResetFilters} />
        <Notification variant={{ validation: 'error' }}>
          <div>
            <strong>Error:</strong> {error}
          </div>
        </Notification>
      </div>
    );
  }

  if (!data) {
    return (
      <div>
        <FilterPanel filters={filters} onFilterChange={handleFilterChange} onApply={handleApplyFilters} onReset={handleResetFilters} />
        <EmptyState title="No Data Available" />
      </div>
    );
  }

  const {
    summary,
    by_category,
    by_method,
    by_status_code,
    performance,
    hourly_distribution,
    top_endpoints,
    slowest_endpoints,
    top_users
  } = data;

  const errorRate = parseFloat((summary && summary.error_rate) || 0);

  // Calculate requests per minute
  const requestsPerMinute = summary && summary.total_requests 
    ? (summary.total_requests / (24 * 60)).toFixed(2)
    : 0;

  // Prepare chart data
  const categoryData = by_category && by_category.map(item => ({
    name: item.endpoint_category || 'Unknown',
    value: parseInt(item.count, 10)
  })) || [];

  const methodData = by_method && by_method.map(item => ({
    name: item.method || 'Unknown',
    count: parseInt(item.count, 10)
  })) || [];

  const statusCodeData = by_status_code && by_status_code.map(item => {
    const code = parseInt(item.status_code || 0, 10);
    let category = 'Other';
    if (code >= 200 && code < 300) category = '2xx Success';
    else if (code >= 400 && code < 500) category = '4xx Client Error';
    else if (code >= 500) category = '5xx Server Error';

    return {
      statusCode: item.status_code || 'Unknown',
      category,
      count: parseInt(item.count, 10)
    };
  }) || [];

  // Group status codes by category for stacked chart
  const statusByCategory = statusCodeData.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = 0;
    acc[item.category] += item.count;
    return acc;
  }, {});

  const statusChartData = Object.entries(statusByCategory).map(([category, count]) => ({
    name: category,
    count
  }));

  const hourlyData = hourly_distribution && hourly_distribution.map(item => ({
    hour: `${item.hour}:00`,
    count: parseInt(item.count, 10)
  })) || [];

  // Prepare top endpoints table
  const topEndpointsTableData = top_endpoints && top_endpoints.slice(0, 20).map((item, index) => {
    const requestCount = item.count || item.count === 0 ? parseInt(item.count, 10) : 0;
    
    return {
      id: index,
      rank: index + 1,
      endpoint: item.endpoint || 'Unknown',
      count: formatNumber(requestCount),
      avgTime: formatMilliseconds(item.avg_response_time_ms),
      avgTimeRaw: item.avg_response_time_ms
    };
  }) || [];

  const topEndpointsColumns = [
    { key: 'rank', label: 'Rank' },
    { key: 'endpoint', label: 'Endpoint' },
    { key: 'count', label: 'Requests' },
    {
      key: 'avgTime',
      label: 'Avg Response Time',
      badge: (value, row) => ({
        background: row.avgTimeRaw < 500 ? '#e8f5e9' : row.avgTimeRaw < 2000 ? '#fff3e0' : '#ffebee',
        color: getPerformanceColor(row.avgTimeRaw)
      })
    }
  ];

  // Prepare slowest endpoints table
  const slowestEndpointsTableData = slowest_endpoints && slowest_endpoints.slice(0, 10).map((item, index) => {
    const requestCount = item.request_count || item.request_count === 0 ? parseInt(item.request_count, 10) : 0;
    
    return {
      id: index,
      endpoint: item.endpoint || 'Unknown',
      category: item.category || 'Unknown',
      avgTime: formatMilliseconds(item.avg_response_time_ms),
      maxTime: formatMilliseconds(item.max_response_time_ms),
      count: formatNumber(requestCount),
      avgTimeRaw: item.avg_response_time_ms
    };
  }) || [];

  const slowestEndpointsColumns = [
    { key: 'endpoint', label: 'Endpoint' },
    { key: 'category', label: 'Category' },
    { key: 'avgTime', label: 'Avg Time' },
    { key: 'maxTime', label: 'Max Time' },
    { key: 'count', label: 'Requests' }
  ];

  // Prepare top users table
  const topUsersTableData = top_users && top_users
    .filter(item => item.user !== null) // Only filter out entries with null user
    .map((item, index) => {
      const firstName = (item.user && item.user.first_name) || '';
      const lastName = (item.user && item.user.last_name) || '';
      const fullName = `${firstName} ${lastName}`.trim();
      
      // Handle null count from backend
      let displayCount;
      if (item.count === null || item.count === undefined) {
        displayCount = 'N/A';
      } else {
        const apiCallCount = parseInt(item.count, 10);
        displayCount = formatNumber(apiCallCount);
      }
      
      return {
        id: (item.user && item.user.id) || index,
        rank: index + 1,
        name: fullName || 'Unknown User',
        email: (item.user && item.user.email) || 'N/A',
        count: displayCount
      };
    }) || [];

  const topUsersColumns = [
    { key: 'rank', label: 'Rank' },
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'count', label: 'API Calls' }
  ];

  return (
    <div>
      {error && (
        <Notification variant={{ validation: 'error' }}>
          <div>
            <strong>Error:</strong> {error}
          </div>
        </Notification>
      )}
      <FilterPanel
        filters={filters}
        onFilterChange={handleFilterChange}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        onExport={handleExportExcel}
        isExporting={isExporting}
      />

      {/* Summary Cards */}
      <FlexGrid>
        <FlexGrid.Row>
          <FlexGrid.Col xs={12} md={6} lg={3} style={{ padding: '0 0.75rem', marginBottom: '1.5rem' }}>
            <SummaryCard
              title="Total Requests"
              value={formatNumber(summary && summary.total_requests)}
              icon="🔌"
            />
          </FlexGrid.Col>
          <FlexGrid.Col xs={12} md={6} lg={3} style={{ padding: '0 0.75rem', marginBottom: '1.5rem' }}>
            <SummaryCard
              title="Error Rate"
              value={`${errorRate.toFixed(2)}%`}
              color={errorRate > 5 ? '#CC0000' : errorRate > 2 ? '#FF9900' : '#00A600'}
              badge={{
                text: `${formatNumber(summary && summary.error_count)} errors`,
                background: errorRate > 5 ? '#ffebee' : '#fff3e0',
                color: errorRate > 5 ? '#CC0000' : '#FF9900'
              }}
            />
          </FlexGrid.Col>
          <FlexGrid.Col xs={12} md={6} lg={3} style={{ padding: '0 0.75rem', marginBottom: '1.5rem' }}>
            <SummaryCard
              title="Avg Response Time"
              value={formatMilliseconds(performance && performance.avg_response_time_ms)}
              color={getPerformanceColor(performance && performance.avg_response_time_ms)}
              subText={`Min: ${formatMilliseconds(performance && performance.min_response_time_ms)} | Max: ${formatMilliseconds(performance && performance.max_response_time_ms)}`}
            />
          </FlexGrid.Col>
          <FlexGrid.Col xs={12} md={6} lg={3} style={{ padding: '0 0.75rem', marginBottom: '1.5rem' }}>
            <SummaryCard
              title="Requests/Minute"
              value={requestsPerMinute}
              icon="⚡"
            />
          </FlexGrid.Col>
        </FlexGrid.Row>
      </FlexGrid>

      {/* Status Code Breakdown — 2xx/3xx/4xx/5xx grouped totals + common error codes, replacing
          the single hard-to-interpret "Error Rate" % as the only signal (QA P1 finding). */}
      {summary && summary.status_groups && (
        <div style={{ marginTop: '0.5rem', marginBottom: '2rem' }}>
          <Typography heading="h3" variant={{ size: 'h3' }} block style={{ marginBottom: '1rem', color: '#4b286d' }}>Status Code Breakdown</Typography>
          <FlexGrid>
            <FlexGrid.Row>
              {[
                { key: '2xx', color: '#00A600' },
                { key: '3xx', color: '#0066CC' },
                { key: '4xx', color: '#FF9900' },
                { key: '5xx', color: '#CC0000' }
              ].map(group => (
                <FlexGrid.Col key={group.key} xs={6} md={3} style={{ padding: '0 0.5rem', marginBottom: '1rem' }}>
                  <SummaryCard
                    title={`${group.key} Responses`}
                    value={formatNumber(summary.status_groups[group.key])}
                    color={group.color}
                  />
                </FlexGrid.Col>
              ))}
            </FlexGrid.Row>
          </FlexGrid>
          {summary.common_codes && (
            <FlexGrid style={{ marginTop: '0.5rem' }}>
              <FlexGrid.Row>
                {Object.entries(summary.common_codes).map(([code, count]) => (
                  <FlexGrid.Col key={code} xs={6} md={4} style={{ padding: '0 0.5rem', marginBottom: '1rem' }}>
                    <SummaryCard title={`HTTP ${code}`} value={formatNumber(count)} icon="🚨" />
                  </FlexGrid.Col>
                ))}
              </FlexGrid.Row>
            </FlexGrid>
          )}
        </div>
      )}

      {/* Charts Section */}
      <FlexGrid style={{ marginTop: '2rem' }}>
        <FlexGrid.Row>
          <FlexGrid.Col xs={12} md={6} style={{ padding: '0 0.75rem', marginBottom: '2rem' }}>
            <div
              style={chartCardStyle}
            >
              <Typography heading="h3" variant={{ size: 'h3' }} block style={{ marginBottom: '1rem', color: '#4b286d' }}>
                Requests by Category
              </Typography>
              <p style={{ fontSize: '0.75rem', color: '#999999', marginBottom: '0.5rem' }}>Click a slice to view the underlying requests</p>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={false}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                    onClick={entry => setDrillDownCategory((entry && entry.name) || (entry && entry.payload && entry.payload.name))}
                    style={{ cursor: 'pointer' }}
                  >
                    {categoryData.map((entry, index) => (
                      <Cell
                        key={`cell-${entry.name}-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatNumber(value)} />
                  <Legend 
                    formatter={(value, entry) => {
                      const itemValue = entry.payload.value;
                      const totalRequests = categoryData.reduce((sum, item) => sum + item.value, 0);
                      const percentage = ((itemValue / totalRequests) * 100).toFixed(2);
                      return `${value}: ${formatNumber(itemValue)} (${percentage}%)`;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </FlexGrid.Col>

          <FlexGrid.Col xs={12} md={6} style={{ padding: '0 0.75rem', marginBottom: '2rem' }}>
            <div
              style={chartCardStyle}
            >
              <Typography heading="h3" variant={{ size: 'h3' }} block style={{ marginBottom: '1rem', color: '#4b286d' }}>
                Requests by Method
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={methodData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#0066CC" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </FlexGrid.Col>
        </FlexGrid.Row>

        <FlexGrid.Row>
          <FlexGrid.Col xs={12} md={6} style={{ padding: '0 0.75rem', marginBottom: '2rem' }}>
            <div
              style={chartCardStyle}
            >
              <Typography heading="h3" variant={{ size: 'h3' }} block style={{ marginBottom: '1rem', color: '#4b286d' }}>
                Status Code Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={statusChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#4b286d" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </FlexGrid.Col>

          <FlexGrid.Col xs={12} md={6} style={{ padding: '0 0.75rem', marginBottom: '2rem' }}>
            <div
              style={chartCardStyle}
            >
              <Typography heading="h3" variant={{ size: 'h3' }} block style={{ marginBottom: '1rem', color: '#4b286d' }}>
                Hourly Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={hourlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" />
                  <YAxis />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#0066CC"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </FlexGrid.Col>
        </FlexGrid.Row>
      </FlexGrid>

      {/* Top Endpoints Table */}
      {topEndpointsTableData.length > 0 && (
        <div style={{ marginTop: '3rem' }}>
          <Typography heading="h3" variant={{ size: 'h3' }} block style={{ marginBottom: '1.5rem', color: '#4b286d' }}>
            Top Endpoints (by Request Count)
          </Typography>
          <DataTable columns={topEndpointsColumns} data={topEndpointsTableData} />
        </div>
      )}

      {/* Slowest Endpoints Table */}
      {slowestEndpointsTableData.length > 0 && (
        <div style={{ marginTop: '3rem' }}>
          <Typography heading="h3" variant={{ size: 'h3' }} block style={{ marginBottom: '1.5rem', color: '#4b286d' }}>
            Slowest Endpoints ⚠️
          </Typography>
          <DataTable columns={slowestEndpointsColumns} data={slowestEndpointsTableData} />
        </div>
      )}

      {/* Most Active API Users Table - Removed as per user request */}

      <DrillDownModal
        open={!!drillDownCategory}
        onClose={() => setDrillDownCategory(null)}
        title={`API Requests — ${drillDownCategory}`}
        queryParams={{
          startDate: filterValues.startDate,
          endDate: filterValues.endDate,
          endpointCategory: drillDownCategory,
          method: filterValues.method,
          statusCode: filterValues.statusCode,
          excludeScanners: filterValues.excludeScanners
        }}
        fetchRecords={reportsAPI.getApiRequestRecords}
        columns={[
          { key: 'method', label: 'Method' },
          { key: 'endpoint', label: 'Endpoint' },
          { key: 'status_code', label: 'Status' },
          { key: 'response_time_ms', label: 'Response (ms)' },
          { key: 'created_at', label: 'Created At' }
        ]}
      />
    </div>
  );
};

export default ApiRequestsReport;
